<template>
  <div class="agile-view">
    <!-- Hero Section -->
    <section class="relative pt-32 pb-20 bg-gradient-to-br from-[#0f4640] to-[#1a6c64]">
      <div class="container mx-auto px-4">
        <div class="text-center">
          <h1 class="text-5xl font-bold text-[#faf6df] mb-6">{{ $t('agile.hero.title') }}</h1>
          <p class="text-xl text-[#faf6df] max-w-3xl mx-auto opacity-90">
            {{ $t('agile.hero.description') }}
          </p>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <section class="py-20">
      <div class="container mx-auto px-4">
        <!-- Tasks Overview -->
        <div class="mb-20">
          <h2 class="text-3xl font-bold text-[#0f4640] mb-8" :class="{ 'text-right': $i18n.locale === 'ar' }">
            {{ $t('agile.sections.tasksOverview.title') }}
          </h2>
          <div class="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <img src="../assets/Tasks.png" :alt="$t('agile.sections.tasksOverview.title')" class="w-full rounded-xl mb-6">
            <p class="text-lg text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
              {{ $t('agile.sections.tasksOverview.description') }}
            </p>
          </div>
        </div>

        <!-- Task Creation -->
        <div class="mb-20">
          <h2 class="text-3xl font-bold text-[#0f4640] mb-8" :class="{ 'text-right': $i18n.locale === 'ar' }">
            {{ $t('agile.sections.taskCreation.title') }}
          </h2>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div class="bg-white rounded-2xl shadow-xl p-8 relative overflow-hidden">
              <div class="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#0f4640] to-[#1a6c64]"></div>
              <img src="../assets/How to Create a Task.gif" :alt="$t('agile.sections.taskCreation.title')" class="w-full rounded-xl mb-6" loading="lazy">
              <div class="absolute bottom-0 left-0 w-full h-full bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <span class="text-white text-lg font-semibold">{{ $t('agile.sections.taskCreation.title') }}</span>
              </div>
            </div>
            <div class="space-y-6" :class="{ 'text-right': $i18n.locale === 'ar' }">
              <div class="bg-[#faf6df] rounded-xl p-6" data-v-7a23b2b4="">
                <h3 class="text-xl font-bold text-[#0f4640] mb-3" data-v-7a23b2b4="">خطوات إنشاء مهمة</h3>
                <ul class="space-y-3 text-gray-700" data-v-7a23b2b4="">
                  <li class="flex items-center gap-3" data-v-7a23b2b4="">
                    <span data-v-7a23b2b4="">1.</span>
                    <span data-v-7a23b2b4="">انقر على زر "إضافة مهمة جديدة"</span>
                  </li>
                  <li class="flex items-center gap-3" data-v-7a23b2b4="">
                    <span data-v-7a23b2b4="">2.</span>
                    <span data-v-7a23b2b4="">أدخل عنوان ووصف المهمة</span>
                  </li>
                  <li class="flex items-center gap-3" data-v-7a23b2b4="">
                    <span data-v-7a23b2b4="">3.</span>
                    <span data-v-7a23b2b4="">حدد المسؤول عن المهمة والموعد النهائي</span>
                  </li>
                  <li class="flex items-center gap-3" data-v-7a23b2b4="">
                    <span data-v-7a23b2b4="">4.</span>
                    <span data-v-7a23b2b4="">أضف أي مرفقات أو تعليقات إضافية</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Timeline and Dependencies -->
        <div class="mb-20">
          <h2 class="text-3xl font-bold text-[#0f4640] mb-8" :class="{ 'text-right': $i18n.locale === 'ar' }">
            {{ $t('agile.sections.timeline.title') }}
          </h2>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div class="bg-white rounded-2xl shadow-xl p-8">
              <img src="../assets/Timeline.png" :alt="$t('agile.sections.timeline.timelineCard.title')" class="w-full rounded-xl mb-6">
              <h3 class="text-xl font-bold text-[#0f4640] mb-3" :class="{ 'text-right': $i18n.locale === 'ar' }">
                {{ $t('agile.sections.timeline.timelineCard.title') }}
              </h3>
              <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
                {{ $t('agile.sections.timeline.timelineCard.description') }}
              </p>
            </div>
            <div class="bg-white rounded-2xl shadow-xl p-8">
              <img src="../assets/Dependencies.png" :alt="$t('agile.sections.timeline.dependenciesCard.title')" class="w-full rounded-xl mb-6">
              <h3 class="text-xl font-bold text-[#0f4640] mb-3" :class="{ 'text-right': $i18n.locale === 'ar' }">
                {{ $t('agile.sections.timeline.dependenciesCard.title') }}
              </h3>
              <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
                {{ $t('agile.sections.timeline.dependenciesCard.description') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Additional Features -->
        <div class="mb-20">
          <h2 class="text-3xl font-bold text-[#0f4640] mb-8" :class="{ 'text-right': $i18n.locale === 'ar' }">
            {{ $t('agile.sections.additionalFeatures.title') }}
          </h2>
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div class="bg-white rounded-2xl shadow-xl p-8">
              <img src="../assets/Additional Options.png" :alt="$t('agile.sections.additionalFeatures.cards.options.title')" class="w-full rounded-xl mb-6" loading="lazy">
              <h3 class="text-xl font-bold text-[#0f4640] mb-3" :class="{ 'text-right': $i18n.locale === 'ar' }">
                {{ $t('agile.sections.additionalFeatures.cards.options.title') }}
              </h3>
              <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
                {{ $t('agile.sections.additionalFeatures.cards.options.description') }}
              </p>
            </div>
            <div class="bg-white rounded-2xl shadow-xl p-8">
              <img src="../assets/Costing.png" :alt="$t('agile.sections.additionalFeatures.cards.costing.title')" class="w-full rounded-xl mb-6" loading="lazy">
              <h3 class="text-xl font-bold text-[#0f4640] mb-3" :class="{ 'text-right': $i18n.locale === 'ar' }">
                {{ $t('agile.sections.additionalFeatures.cards.costing.title') }}
              </h3>
              <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
                {{ $t('agile.sections.additionalFeatures.cards.costing.description') }}
              </p>
            </div>
            <div class="bg-white rounded-2xl shadow-xl p-8">
              <img src="../assets/the Timesheets.png" :alt="$t('agile.sections.additionalFeatures.cards.timesheets.title')" class="w-full rounded-xl mb-6">
              <h3 class="text-xl font-bold text-[#0f4640] mb-3" :class="{ 'text-right': $i18n.locale === 'ar' }">
                {{ $t('agile.sections.additionalFeatures.cards.timesheets.title') }}
              </h3>
              <p class="text-gray-600" :class="{ 'text-right': $i18n.locale === 'ar' }">
                {{ $t('agile.sections.additionalFeatures.cards.timesheets.description') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Call to Action -->
        <div class="text-center">
          <router-link 
            to="/contact" 
            class="inline-flex items-center gap-2 px-8 py-4 bg-[#0f4640] text-[#faf6df] rounded-xl font-bold hover:bg-opacity-90 transition-all duration-300"
          >
            {{ $t('agile.cta.text') }}
            <i :class="[$i18n.locale === 'ar' ? 'fas fa-arrow-left' : 'fas fa-arrow-right']"></i>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'AgileView'
}
</script>

<style scoped>
.agile-view {
  min-height: 100vh;
}
</style> 